
import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HelpCircle, Loader2, Play } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ContextualHelp, ExtendedHelp } from './ContextualHelp';
import { EnhancedCertificateExport } from './EnhancedCertificateExport';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIsMobile } from '@/hooks/use-mobile';
import { ResponsiveTable } from '@/components/ui/responsive-table';

// Import our new components
import { PoliciesTable } from './retention/PoliciesTable';
import { PoliciesTableLoading } from './retention/PoliciesTableLoading';
import { AddPolicyDialog } from './retention/AddPolicyDialog';
import { useRetentionPolicies } from '@/hooks/useRetentionPolicies';

export function RetentionPolicyTable() {
  const { toast } = useToast();
  const [isExportDialogOpen, setIsExportDialogOpen] = React.useState(false);
  const isMobile = useIsMobile();
  
  const { 
    policies, 
    isLoading, 
    error, 
    refetch,
    isAddDialogOpen,
    setIsAddDialogOpen,
    isProcessing,
    handleRunRetention,
    onSubmitPolicy,
    onDeletePolicy,
    isSubmitting,
    isDeleting
  } = useRetentionPolicies();
  
  return (
    <Card>
      <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-2">
          <div>
            <CardTitle>Retention Policies</CardTitle>
            <CardDescription>
              Configure how long certificates and other records are retained before archiving
            </CardDescription>
          </div>
          <ContextualHelp topic="retention-policy" placement="right" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            onClick={() => setIsExportDialogOpen(true)}
            className="gap-2"
            size={isMobile ? "sm" : "default"}
          >
            Export Records
          </Button>
          
          <AddPolicyDialog
            open={isAddDialogOpen}
            onOpenChange={setIsAddDialogOpen}
            onSubmit={onSubmitPolicy}
            isSubmitting={isSubmitting}
          />
          
          <Button 
            variant="secondary" 
            onClick={handleRunRetention}
            disabled={isProcessing}
            size={isMobile ? "sm" : "default"}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run Retention
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="mb-4">
          <ExtendedHelp topic="retention-policy" variant="inline" size="lg" buttonText="Learn about retention policies">
            <div className="space-y-4">
              <p>
                Data retention policies determine how long certificate records and related data are kept before 
                they're archived or deleted. Different states have different regulatory requirements for data
                retention periods.
              </p>
              
              <div>
                <h5 className="font-medium mb-2">State-specific Requirements</h5>
                <ResponsiveTable compact={isMobile}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>State</TableHead>
                        <TableHead>Minimum Retention</TableHead>
                        <TableHead>Recommendation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>Florida</TableCell>
                        <TableCell>5 years</TableCell>
                        <TableCell>7 years</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>California</TableCell>
                        <TableCell>3 years</TableCell>
                        <TableCell>5 years</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Texas</TableCell>
                        <TableCell>3 years</TableCell>
                        <TableCell>5 years</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Other States</TableCell>
                        <TableCell>Varies</TableCell>
                        <TableCell>5 years</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </ResponsiveTable>
              </div>
              
              <div>
                <h5 className="font-medium mb-2">Process Overview</h5>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Configure retention policies for each data type and state</li>
                  <li>Schedule regular retention processes (typically quarterly)</li>
                  <li>Records exceeding retention periods are archived</li>
                  <li>Archives are retained for an additional period before permanent deletion</li>
                  <li>All retention actions are logged for audit purposes</li>
                </ol>
              </div>
            </div>
          </ExtendedHelp>
        </div>
        
        <PoliciesTableLoading 
          isLoading={isLoading} 
          isError={!!error} 
          refetch={refetch} 
        />
        
        {!isLoading && !error && (
          <ResponsiveTable compact={isMobile}>
            <PoliciesTable 
              policies={policies || []} 
              onEditPolicy={(policy) => {
                toast({
                  title: "Edit Policy",
                  description: "Policy editing is not implemented yet.",
                });
              }} 
              onDeletePolicy={onDeletePolicy}
            />
          </ResponsiveTable>
        )}
      </CardContent>
      
      <CardFooter className="border-t bg-muted/50 text-muted-foreground text-sm flex flex-col md:flex-row md:justify-between md:items-center gap-2">
        <p>
          Retention policies automatically archive old certificates and records according to state regulatory requirements.
        </p>
        <Button variant="link" size="sm" className="flex items-center gap-1">
          <HelpCircle className="h-3 w-3" />
          View Compliance Guide
        </Button>
      </CardFooter>
      
      {/* Enhanced Export Dialog */}
      <EnhancedCertificateExport
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
      />
    </Card>
  );
}
